// ── goals.js — 매장 목표 관리 ───────────────────────────────────
// localStorage 기반 (per-month). 사용자별 키 분리.
//
// 4개 목표:
//   1. 월매출 (won)            — 절대값
//   2. 순이익률 (%)            — 양호 30%+
//   3. 식재료 원가율 (%)        — 35% 이하 양호
//   4. 인건비 비율 (%)          — 25% 이하 양호
//
// 계산: 달성률 = 실적 / 목표 × 100
// 음의 지표(원가율·인건비)는 반대로: 목표 / 실적 × 100 (낮을수록 좋음)

const STORAGE_KEY = 'semfit_goals';

export const GOAL_DEFS = [
  {
    id: 'revenue',
    label: '월 매출',
    icon: '💰',
    unit: 'won',
    color: '#18181B',
    direction: 'higher',   // 높을수록 좋음
    defaultTarget: 10000000,  // 1천만원
    placeholder: '예: 15000000',
    description: '이번달 총 매출 목표 (원)',
  },
  {
    id: 'netMargin',
    label: '순이익률',
    icon: '📈',
    unit: 'percent',
    color: '#EA580C',
    direction: 'higher',
    defaultTarget: 20,
    placeholder: '예: 25',
    description: '총 매출 대비 순이익 비율 (%)',
  },
  {
    id: 'foodCost',
    label: '식재료 원가율',
    icon: '🥬',
    unit: 'percent',
    color: '#EA580C',
    direction: 'lower',    // 낮을수록 좋음
    defaultTarget: 35,
    placeholder: '예: 33',
    description: '매출 대비 식재료비 (%) — 낮을수록 좋음',
  },
  {
    id: 'laborCost',
    label: '인건비 비율',
    icon: '👥',
    unit: 'percent',
    color: '#1A56A0',
    direction: 'lower',
    defaultTarget: 25,
    placeholder: '예: 22',
    description: '매출 대비 인건비 (%) — 낮을수록 좋음',
  },
];

// ── localStorage I/O ──
export function loadGoals() {
  if (typeof window === 'undefined') return defaultGoals();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultGoals();
    const parsed = JSON.parse(raw);
    return { ...defaultGoals(), ...parsed };
  } catch (e) {
    return defaultGoals();
  }
}

export function saveGoals(goals) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(goals));
  } catch (e) { /* swallow */ }
}

function defaultGoals() {
  const obj = {};
  GOAL_DEFS.forEach(g => { obj[g.id] = g.defaultTarget; });
  return obj;
}

// ── 매출 run-rate 외삽 (현재 진행 중인 월일 때만) ──
// 1~오늘 매출이 X원이면, 월말 예상치 = X × (월일수 / 오늘일자)
// 인건비 등 월 단위 비용과 같은 기간 기준으로 비율 비교를 위함
function applyRunRate(actualRevenue, monthKey) {
  if (!monthKey || actualRevenue <= 0) return actualRevenue;
  const now = new Date();
  const todayMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (monthKey !== todayMonthKey) return actualRevenue;  // 과거 월은 외삽 X
  const todayDay = now.getDate();
  if (todayDay < 7) return actualRevenue;  // 월 초 7일 미만은 변동성 커서 외삽 X
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return Math.round(actualRevenue * (daysInMonth / todayDay));
}

// ── 실적 계산 (월별 record 기반) ──
//   options.monthKey 있으면 현재 월일 때 매출 run-rate 외삽 (목표·비율 평가 정확화)
export function calcGoalActuals(record, options = {}) {
  const r = record?.revenue || {};
  const c = record?.costs || {};
  const actualRevenue = (Number(r.card) || 0) + (Number(r.cash) || 0) + (Number(r.delivery) || 0);
  const totalRevenue = applyRunRate(actualRevenue, options.monthKey);
  const ingredients = Number(c.ingredients) || 0;
  const labor = Number(c.labor) || 0;
  const totalCosts = ingredients + labor + (Number(c.rent) || 0) + (Number(c.utilities) || 0) + (Number(c.other) || 0);
  const netProfit = totalRevenue - totalCosts;

  return {
    revenue: totalRevenue,
    netMargin: totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : null,
    foodCost: totalRevenue > 0 ? (ingredients / totalRevenue) * 100 : null,
    laborCost: totalRevenue > 0 ? (labor / totalRevenue) * 100 : null,
    _totalRevenue: totalRevenue,
    _totalCosts: totalCosts,
    _netProfit: netProfit,
  };
}

// ── 달성률 계산 ──
//   higher: 실적/목표 (목표 대비 얼마나 달성)
//   lower:  목표/실적 (실적이 목표보다 낮을수록 좋음)
//   결과: 0~150% 범위 (게이지에서 100% 만점)
export function calcAchievement(goalDef, target, actual) {
  if (actual === null || actual === undefined) return null;
  if (target <= 0) return null;
  if (goalDef.direction === 'higher') {
    if (actual <= 0) return 0;
    return (actual / target) * 100;
  } else {
    // lower
    if (actual <= 0) return 100;  // 비용 0 = 만점
    return (target / actual) * 100;
  }
}

// ── 색상 결정 ──
export function getAchievementColor(pct) {
  if (pct === null) return '#9ca3af';
  if (pct >= 100) return '#FF6F0F';   // 달성
  if (pct >= 80)  return '#C2410C';    // 근접
  if (pct >= 60)  return '#EA580C';    // 주의
  return '#52525B';                     // 미달
}

export function getAchievementLabel(pct) {
  if (pct === null) return '데이터 없음';
  if (pct >= 100) return '🎯 달성';
  if (pct >= 80)  return '👍 거의 달성';
  if (pct >= 60)  return '⚠️ 분발';
  return '🔴 미달';
}
